//#region Asset Imports

import * as _ from 'underscore'

//#endregion

import { SystemService } from '../../../../services/system'

import { Enums } from '../../../../models/common/enums'

import { Provider, ProviderScope, Map, MapEvent } from './provider'

import { OpMarker, OpCoord, OpPolygon, OpRange, OpBounds, OpTooltip, OpPolyline, Polygon, Polyline, Range, Marker, Tooltip } from '../../../../models/custom/op-map-area'

declare var L: any

// #region Private Types 

interface LatLngLiteral {
    lat: number
    lng: number
}
type LatLng = {
    lat: number
    lng: number
    alt?: number
}

type LatLngBoundsExpression = LatLngBounds | LatLngBoundsLiteral
type LatLngTuple = [number, number]
type LatLngBoundsLiteral = LatLngTuple[]
type LatLngExpression = LatLng | LatLngLiteral | LatLngTuple

type LatLngBounds = {
    extend(latlngOrBounds: LatLngExpression | LatLngBoundsExpression)
    getCenter(): LatLng
}

type LeafleMapOptions = {
    attribution: string
    id: string
    center: { lat: number, lng: number }
    zoom: number
    minZoom: number
    maxZoom: number
    mapTypeControl: boolean
    fullscreenControl: boolean
}

// #endregion 


export class StreetMapProvider extends Provider {

    // #region Const

    private static get LOG_PREFIX(): string { return '[SIMAX.Common.Map.Provider.StreetMapProvider]' }
    private static get LOG_HELPER(): string { return 'No default marker icon options. Using the default icon maps one.' }
    private static get ATTRIBUTION(): string { return 'Map data &copy; <a href="https://www.openstreetmap.org/">OpenStreetMap</a> contributors, ' + '<a href="https://creativecommons.org/licenses/by-sa/2.0/">CC-BY-SA</a>, ' + 'Imagery © <a href="https://www.mapbox.com/">Mapbox</a>' }
    private static get ID(): string { return 'mapbox.streets' }
    private static get MIN_ZOOM(): number { return 2 }
    private static get MAX_ZOOM(): number { return 15 }
    private static get URL_API(): string { return 'https://api.tiles.mapbox.com/v4/{id}/{z}/{x}/{y}.png?access_token=pk.eyJ1IjoibWFwYm94IiwiYSI6ImNpejY4NXVycTA2emYycXBndHRqcmZ3N3gifQ.rJcFIG214AriISLbB6B5aw' }
    private static get MAP_ICON(): string { return '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 10 10"><path transform="translate(5,8)" fill="{fillColor}" stroke="{strokeColor}" stroke-width="{strokeWeight}" stroke-width="strokeWeight" d="{path}"/></svg>' }
    private static get MARKER_BOUNCE_COUNT(): number { return 5 }
    // #endregion


    // #region Properties
    private map: any
    private drawingManager: any
    private mapCluster: any
    private markers: any[] = []
    private boundsMarkers: any[] = []
    private selectedMarkers: OpMarker[] = []
    private selectedElements: any[] = []
    private polygons: any[] = []
    private ranges: any[] = []
    private mapOptions: LeafleMapOptions
    private mapRange: any = new L.FeatureGroup()
    private mapPolygon: any = new L.FeatureGroup()

    // #endregion


    // #region Ctor

    constructor(private $scope: ProviderScope, element: HTMLElement, onMapReady: (provider: Provider) => void, systemService: SystemService) {
        super($scope, systemService)

        const tileLayer: any = L.tileLayer(StreetMapProvider.URL_API, { attribution: StreetMapProvider.ATTRIBUTION, id: StreetMapProvider.ID })

        let center: OpCoord = new OpCoord()
        center.latitude = 0
        center.longitude = 0

        if ($scope.options.initCenter != null) {
            center = $scope.options.initCenter
        }

        let zoom: number = 5
        if ($scope.options.initZoom >= 0) {
            zoom = $scope.options.initZoom
        }

        this.mapOptions = {
            attribution: StreetMapProvider.ATTRIBUTION,
            id: StreetMapProvider.ID,
            center: { lat: center.latitude, lng: center.longitude },
            zoom: zoom,
            minZoom: StreetMapProvider.MIN_ZOOM,
            maxZoom: StreetMapProvider.MAX_ZOOM,
            mapTypeControl: true,
            fullscreenControl: false
        }

        if ($scope.options.disableMapTypeControl != null) {
            this.mapOptions.mapTypeControl = false
        }

        this.map = new L.map(element, this.mapOptions)

        tileLayer.addTo(this.map)

        this.resize()
        onMapReady(this)

        if ($scope.options.onMapClick != null) {
            this.editCoord()
        }
    }

    // #endregion


    // #region Bounds Methods

    public getBounds(): OpBounds {
        if (this.map != null) {
            const bounds: LatLngBounds = this.map.getBounds()
            const opBounds: OpBounds = this.streetMapBoundsToOpBounds(bounds)

            return opBounds
        }
        return null
    }

    public setBounds(bounds: OpBounds): void {
        if (this.map != null) {
            let bound: LatLngBounds = new L.latLngBounds()

            if (bounds != null) {
                bound.extend(new L.latLng(bounds.southWest.latitude, bounds.southWest.longitude))
                bound.extend(new L.latLng(bounds.northEast.latitude, bounds.northEast.longitude))
                this.map.fitBounds(bound)
            }
        }
    }

    public fitBounds(positions: OpCoord[]): void {
        if (this.map != null) {
            let bounds: LatLngBounds = new L.latLngBounds()
            _.forEach(positions, (position: OpCoord) => {
                bounds.extend(new L.latLng(position.latitude, position.longitude))
            })

            this.map.fitBounds(bounds)
        }
    }

    // #endregion


    // #region Center Methods

    public setCenter(center: OpCoord): void {
        if (this.map != null) {
            const latLng: LatLng = new L.LatLng(center.latitude, center.longitude)
            this.map.setView(latLng)
        }
    }

    public center(): void {
        if (this.map != null) {
            const bounds: LatLngBounds = this.getMarkersBounds()

            if (bounds != null) {
                this.map.setView(bounds.getCenter())
            }
        }
    }

    public centerIn(): void {
        if (this.map != null) {
            const bounds: LatLngBounds = this.getSelectedMarkersBounds()

            if (bounds != null) {
                setTimeout(() => {
                    this.map.setView(bounds.getCenter())
                }, 200)
            }
        }
    }

    public centerOnMarkers(markers: OpMarker[], isAnimated: boolean): void {
        this.setSelectedMarkers(markers)
        if (markers == null || markers.length === 0) {
            this.center()
        } else {
            this.centerIn()

            setTimeout(() => {
                this.drawSelectedElement(isAnimated)
            }, 200)
        }
    }

    // #endregion


    // #region Zoom Methods

    public getZoom(): number {
        if (this.map != null) {
            return this.map.getZoom()
        }
        return null
    }

    public setZoom(zoom: number): void {
        if (this.map != null) {
            return this.map.setZoom(zoom)
        }
        return null
    }

    public zoomCenter(): void {
        const bounds: LatLngBounds = new L.latLngBounds()

        _.forEach(this.boundsMarkers, (marker: OpMarker) => {
            const boundscoord: any = new L.latLng(marker.coords.latitude, marker.coords.longitude)
            bounds.extend(boundscoord)
        })

        setTimeout(() => {
            this.setFitPane(bounds)
        }, 200)
    }

    public zoomIn(): void {
        const bounds: LatLngBounds = new L.latLngBounds()

        if (this.selectedMarkers != null) {

            _.forEach(this.selectedMarkers, (selectedMarker: OpMarker) => {
                if (this.checkLatitude(selectedMarker.coords.latitude) && this.checkLongitude(selectedMarker.coords.longitude)) {
                    const boundscoords: any = new L.latLng(selectedMarker.coords.latitude, selectedMarker.coords.longitude)
                    bounds.extend(boundscoords)
                }
            })

            setTimeout(() => {
                this.setFitPane(bounds)
            }, 200)
        }
    }

    public zoomOnMarkers(markers: OpMarker[], isAnimated: boolean): void {

        this.selectedMarkers = markers

        if (markers == null || markers.length === 0) {
            this.clearSelectedElements()
            this.zoomCenter()
        } else {
            this.zoomIn()

            setTimeout(() => {
                this.drawSelectedElement(isAnimated)
            }, 200)
        }
    }

    // #endregion


    // #region Markers

    public clearMarkers(): void {
        this.markers = []
    }

    public setMarkers(markers: OpMarker[]): void {
        let cluster: any = new L.markerClusterGroup({
            spiderfyOnMaxZoom: this.$scope.options.clusterZoom,
            zoomToBoundsOnClick: true
        })

        this.clearMarkers()
        if (this.mapCluster != null) {
            this.map.removeLayer(this.mapCluster)
        }
        _.forEach(markers, (marker: OpMarker) => {
            if (this.checkLatitude(marker.coords.latitude) && this.checkLongitude(marker.coords.longitude)) {
                const newMarker: any = this.createMarker(marker)

                if (newMarker != null) {
                    cluster.addLayer(newMarker.getMapMarker())
                    this.markers[marker.referenceValue] = newMarker.getMapMarker()
                }
            }
        })

        this.boundsMarkers = markers

        cluster.addTo(this.map)

        this.mapCluster = cluster

        if (this.$scope.options.isClustering) {
            this.mapCluster = cluster
        } else {
            this.mapCluster.disableClustering()
        }
    }

    public addMarker(marker: OpMarker): OpMarker {
        const mapMarker: any = this.createMarker(marker)

        mapMarker.getMapMarker().addTo(this.map)

        return mapMarker
    }

    // #endregion


    // #region AreaSelect Methods

    public enableAreaSelect(callback: (bounds: any) => void): void {

        this.drawingManager = new L.Draw.Rectangle(this.map)
        this.drawingManager.enable()

        this.map.on('draw:created', (e: any) => {
            let layer: any = e.layer

            const opBounds: OpBounds = this.streetMapBoundsToOpBounds(layer.getBounds())

            if (callback(opBounds) != null) {
                this.drawingManager.disable()
            }
        })
    }

    public disableAreaSelect(): void {
        this.drawingManager.disable()
    }

    // #endregion


    // #region computeDistanceBetweenCoords

    public computeDistanceBetweenCoords(c1: OpCoord, c2: OpCoord): number {
        const point1: LatLng = new L.LatLng(c1.latitude, c1.longitude)
        const point2: LatLng = new L.LatLng(c2.latitude, c2.longitude)

        const point: number = Math.sqrt(((point1[0] - point2[0]) * (point1[0] - point2[0])) + ((point1[1] - point2[1]) * (point1[1] - point2[1])))

        return point
    }

    // #endregion


    // #region clearListeners

    public clearListeners(event: MapEvent, marker: OpMarker): void {
        marker.getMapMarker().off(MapEvent[event].toLowerCase())
    }

    // #endregion


    // #region createMarkerTooltip

 public createMarkerTooltip(marker: OpMarker, text: string): OpTooltip {
        const popup: any = new L.popup({
            maxWidth: 500
        })
            .setContent(text)
            .setLatLng(marker.getMapMarker().getLatLng())

        const tooltipDecorator: Tooltip = {
            show: () => {
                if (marker.isSetOnMap()) {
                    popup.openOn(this.map)
                }
            },

            hide: () => {
                if (popup.isOpen()) {
                    popup.removeFrom(this.map)
                }
            }
        }

        const opTooltip: OpTooltip = new OpTooltip()

        opTooltip.setTooltip(tooltipDecorator)

        return opTooltip
    }

    // #endregion


    // #region resize Map

    public resize(): void {
        setTimeout(() => {
            this.map.invalidateSize(false)
        }, 100)
    }

    // #endregion


    // #region Polygon Methods

    public clearPolygons(): void {
        this.polygons = []
    }

    public addPolygon(polygon: OpPolygon): OpPolygon {
        const coords: { lat: number, lng: number }[] = polygon.coords.map((c: OpCoord) => {
            return {
                lat: c.latitude,
                lng: c.longitude
            }
        })

        const mapPolygon: any = new L.polygon(coords, {
            color: polygon.lineColor,
            opacity: 0.8,
            weight: 2,
            fillColor: polygon.fillColor,
            fillOpacity: 0.35
        })

        if (polygon.tooltip != null) {
            mapPolygon.bindPopup(polygon.tooltip)
        }

        return this.createrPolygonDecorator(mapPolygon, polygon)
    }

    public setPolygons(polygons: OpPolygon[]): void {
        this.clearPolygons()
        if (polygons != null) {
            _.forEach(polygons, (polygon: OpPolygon) => {
                const newPolygon: OpPolygon = this.addPolygon(polygon)
                this.mapPolygon.addLayer(newPolygon.getPolygon().getMapPolygon())
            })

            this.polygons = polygons

            if (_.size(this.polygons) === 0) {
                this.map.removeLayer(this.mapPolygon)
                this.mapPolygon = new L.FeatureGroup()
            }
        }
        this.mapPolygon.addTo(this.map)
    }

    // #endregion


    // #region Ranges Methods

    public clearRanges(): void {
        this.ranges = []
    }

    public addRange(range: OpRange): OpRange {
        const mapCircle: any = new L.circle([range.coords.latitude, range.coords.longitude], {
            color: range.lineColor,
            opacity: range.lineWeight,
            weight: range.lineWeight,
            fillColor: range.fillColor,
            fillOpacity: range.fillOpacity,
            radius: range.radius
        })

        if (range.tooltip != null) {
            mapCircle.bindPopup(range.tooltip)
        }

        return this.createrRangeDecorator(mapCircle, range)
    }

    public setRanges(ranges: OpRange[]): void {
        this.clearRanges()
        if (ranges != null) {
            _.forEach(ranges, (range: OpRange) => {
                const newRange: OpRange = this.addRange(range)
                this.mapRange.addLayer(newRange.getRange().getMapRange())
            })

            this.ranges = ranges

            if (_.size(this.ranges) === 0) {
                this.map.removeLayer(this.mapRange)
                this.mapRange = new L.FeatureGroup()
            }
        }
        this.mapRange.addTo(this.map)
    }

    // #endregion


    // #region Polyline Methods

    public addPolyline(polyline: OpPolyline): OpPolyline {
        const mapPolyline: any = new L.polyline(polyline.positions.map((position: OpCoord) => {
            return {
                lat: position.latitude,
                lng: position.longitude
            }
        }), {
            color: polyline.lineColor,
            opacity: polyline.lineOpacity,
            weight: polyline.lineWeight
        })

        return this.createrPolylineDecorator(mapPolyline, polyline)
    }

    // #endregion


    // #region Private Methods

     private createMarker(marker: OpMarker): any {
        if (!this.checkLatitude(marker.coords.latitude) || !this.checkLongitude(marker.coords.longitude)) {
            return null
        }

        const icon: any = this.getMarkerIcon(marker, this.$scope.options)
        let leafletIcon = null

        if (_.isString(icon)) {
            leafletIcon = new L.icon({
                iconUrl: icon,
                iconAnchor: [12, 32],
                iconSize: [25, 30],
                popupAnchor: [0, -28]
            })
        }
        else {
            const iconSettings: any = {
                mapIconUrl: StreetMapProvider.MAP_ICON,
                fillColor: icon.fillColor,
                path: icon.path,
                strokeColor: icon.strokeColor,
                strokeWeight: icon.strokeWeight
            }

            leafletIcon = new L.divIcon({
                className: 'vector-marker',
                html: L.Util.template(iconSettings.mapIconUrl, iconSettings),
                iconAnchor: [12, 32],
                iconSize: [25, 30],
                popupAnchor: [0, -28]
            })
        }

        if (icon == null) {
            console.warn(StreetMapProvider.LOG_PREFIX)
        }

        let mapMarker: any = new L.marker([marker.coords.latitude, marker.coords.longitude], { icon: leafletIcon })

        if (marker.tooltip != null && !this.$scope.options.hideTooltip) {
            mapMarker.bindPopup(marker.tooltip)
        }

        let markerClickTimeout: any = null
        mapMarker.on('dblclick', () => {
            if (markerClickTimeout != null) {
                clearTimeout(markerClickTimeout)
                markerClickTimeout = null
            }

            if (marker.onDoubleClick != null) {
                marker.onDoubleClick(marker)
            }
        })

        mapMarker.on('click', () => {
            if (marker.onClick != null) {
                marker.onClick(marker)
            }

            this.selectedMarkers = [marker]
            this.$scope.showZoomInButton = true
            this.drawSelectedElement(true)

        })

        return this.createrMarkerDecorator(mapMarker, marker)
    }

    private streetMapBoundsToOpBounds(bounds: any): OpBounds {

        let opBounds: OpBounds = null

        if (bounds != null && bounds.getSouthWest != null && bounds.getNorthEast != null) {
            try {
                const sw: any = bounds.getSouthWest()
                const ne: any = bounds.getNorthEast()

                const swCoord: OpCoord = new OpCoord()
                swCoord.latitude = sw.lat
                swCoord.longitude = sw.lng

                const neCoord: OpCoord = new OpCoord()
                neCoord.latitude = ne.lat
                neCoord.longitude = ne.lng

                opBounds = new OpBounds()
                opBounds.northEast = neCoord
                opBounds.southWest = swCoord
            } catch (e) {
                console.log("Failed to convert maps bounds to OpBounds: " + e)
            }
        }

        return opBounds
    }

    private getSelectedMarkersBounds(): LatLngBounds {
        if (this.selectedMarkers != null && this.selectedMarkers.length > 0) {
            const bounds: LatLngBounds = new L.LatLngBounds()

            _.forEach(this.selectedMarkers, (selectedMarker: OpMarker) => {
                if (this.checkLatitude(selectedMarker.coords.latitude) && this.checkLongitude(selectedMarker.coords.longitude)) {
                    bounds.extend(new L.latLng(selectedMarker.coords.latitude, selectedMarker.coords.longitude))
                }
            })

            return bounds
        }

        return null
    }

    private setFitPane(markerBounds: any): void {
        if (this.map != null) {
            if (_.size(markerBounds) === 0) {
                this.map.fitBounds([[0, 0], [0, 0]])
                this.setZoom(this.$scope.options.maxZoom)
            } else {
                this.map.fitBounds(markerBounds)
                this.map.flyToBounds(markerBounds)

                if (this.map.getZoom() > this.$scope.options.maxZoom) {
                    this.map.setZoom(this.$scope.options.maxZoom)
                }
            }
        }
    }

    private getMarkersBounds(): LatLngBounds {
        if (_.size(this.boundsMarkers) != null) {
            const bounds: LatLngBounds = new L.LatLngBounds()

            _.forEach(this.boundsMarkers, (marker: OpMarker) => {
                bounds.extend(new L.latLng(marker.coords.latitude, marker.coords.longitude))
            })

            return bounds
        }

        return null
    }

    private drawSelectedElement(isAnimated: boolean): void {
        _.forEach(this.selectedMarkers, (selectedMarker: OpMarker) => {
            if (selectedMarker != null && selectedMarker.referenceValue != null) {
                const marker: any = this.markers[selectedMarker.referenceValue]

                if (marker != null) {
                    if (isAnimated) {
                        this.mapCluster.zoomToShowLayer(marker)
                        setTimeout(() => {
                            this.animateMarker(marker)
                        }, 200)
                    }

                    this.selectedElements.push(marker)
                }
            }
        })
    }

    private setSelectedMarkers(markers: OpMarker[]): OpMarker[] {
        this.selectedMarkers = markers
        return this.selectedMarkers
    }

    private createrMarkerDecorator(decoratorMarker: any, marker: OpMarker): OpMarker {
        if (decoratorMarker != null) {
            const markerDecorator: Marker = {
                isShown: (): boolean => {
                    return this.map.hasLayer(decoratorMarker)
                },
                show: (): void => {
                    decoratorMarker.addTo(this.map)
                },
                hide: (): void => {
                    decoratorMarker.removeFrom(this.map)
                },
                setZIndex: (index: number): void => {
                    decoratorMarker.setZIndexOffset(index)
                },
                addListener: (event: Enums.SimaxMarkerEvent, listener: (event) => void): any => {
                    decoratorMarker.on(Enums.SimaxMarkerEvent[event].toLowerCase(), listener)
                },
                getPosition: (): OpCoord => {
                    const position: LatLng = decoratorMarker.getLatLng()

                    if (position != null) {
                        const coord: OpCoord = new OpCoord()
                        coord.latitude = position.lat
                        coord.longitude = position.lng
                        return coord
                    }
                },
                setIcon: (iconUrl: string, scaledSize: { width: number, height: number } = null): void => {
                    let iconSize: [number, number] = [32, 32]
                    let iconAnchor: [number, number] = [16, 32]

                    if (scaledSize != null) {
                        iconSize = [scaledSize.width, scaledSize.height]
                        iconAnchor = [scaledSize.width / 2, scaledSize.height]
                    }

                    const icon = new L.icon({
                        iconAnchor,
                        iconSize,

                        iconUrl: iconUrl,
                        popupAnchor: [0, -28]
                    })

                    decoratorMarker.setIcon(icon)
                },
                getMapMarker: (): any => {
                    return decoratorMarker
                },
                setPosition: (positon: OpCoord): void => {
                    if (positon != null) {
                        decoratorMarker.setLatLng(new L.latLng(positon.latitude, positon.longitude), 10)
                    }
                }
            }

            marker.setMarker(markerDecorator)

            return marker
        }

        return null
    }

    private createrPolygonDecorator(decoratorPolygon: any, polygon: OpPolygon): OpPolygon {
        if (decoratorPolygon != null) {
            const polygonDecorator: Polygon = {
                isShown: (): boolean => {
                    return this.map.hasLayer(decoratorPolygon)
                },
                show: (): void => {
                    decoratorPolygon.addTo(this.map)
                },
                hide: (): void => {
                    decoratorPolygon.removeFrom(this.map)
                },
                getMapPolygon: (): any => {
                    return decoratorPolygon
                }
            }

            polygon.setPolygon(polygonDecorator)

            return polygon
        }

        return null
    }

    private createrRangeDecorator(decoratorRange: any, range: OpRange): OpRange {
        if (decoratorRange != null) {
            const rangeDecorator: Range = {
                isShown: (): boolean => {
                    return this.map.hasLayer(decoratorRange)
                },
                show: (): void => {
                    decoratorRange.addTo(this.map)
                },
                hide: (): void => {
                    decoratorRange.removeFrom(this.map)
                },
                getMapRange: (): void => {
                    return decoratorRange
                }
            }

            range.setRange(rangeDecorator)

            return range
        }

        return null
    }

    private createrPolylineDecorator(decoratorPolyline: any, polyline: OpPolyline): OpPolyline {
        if (decoratorPolyline != null) {
            const polylineDecorator: Polyline = {
                isShown: (): boolean => {
                    return this.map.hasLayer(decoratorPolyline)
                },
                show: (): void => {
                    decoratorPolyline.addTo(this.map)
                },
                hide: (): void => {
                    decoratorPolyline.removeFrom(this.map)
                },
                getMapPolyline: (): any => {
                    return decoratorPolyline
                }
            }

            polyline.setPolyline(polylineDecorator)

            return polyline
        }

        return null
    }

    private editCoord(): void {
        this.map.on('click', (e: any) => {
            const coord = new OpCoord()
            coord.latitude = e.latlng.lat
            coord.longitude = e.latlng.lng

            if (e.latlng != null) {
                this.$scope.options.onMapClick(coord)
            }
        })
    }

    private animateMarker(marker: any): void {
        if (marker != null) {
            marker.bounce(StreetMapProvider.MARKER_BOUNCE_COUNT)
        }
    }

    private clearSelectedElements(): void {
        this.selectedElements = []
    }

    // #endregion
}